import { RefObject, useCallback, useEffect } from "react";

import {
  FOCUSABLE_FIELD_SELECTOR,
  HANDLING_UNITS_FIELD_KEY,
  VALIDATION_FOCUS_ATTR,
} from "../utils/constants";
import {
  isMandatoryMessage,
  ItemIssue,
  mandatoryMessage,
  pruneMandatoryMessages,
  ValidationResult,
} from "../utils/orderValidation";

/**
 * Turns a {@link ValidationResult} into visible feedback: writes an
 * "<Field Name> is mandatory." message under every blank mandatory field, then scrolls
 * to and focuses whichever offending control sits highest on the page.
 *
 * Fields are addressed by a `data-field="<key>"` attribute rather than `id`, because the
 * order screens carry several duplicated ids (`MidStopZipcode` alone appears five times
 * in CustomerOrder/index.tsx) and the dropdown/time-picker components render no id at all.
 */

export interface ApplyValidationOutcome {
  /** False when the caller should abort the save/quote action. */
  ok: boolean;
  /**
   * Set when the handling-unit table is the first thing the user needs to fix, so the
   * caller can open that row for editing and focus the cell.
   */
  focusItemCell: { rowIndex: number; columnKey: string } | null;
}

interface UseMandatoryFieldFocusParams {
  /** Wrapper around the form, used to scope DOM lookups. */
  containerRef: RefObject<HTMLElement | null>;
  setErrors: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  /**
   * The form's scalar values. Watched so a mandatory message disappears the moment its
   * field is filled — including by the bulk prefills that bypass `handleFieldChange`.
   */
  values?: Record<string, any>;
  /** Overrides the `data-field` value used for the handling-unit table. */
  itemsFieldKey?: string;
}

const firstVisible = (
  container: HTMLElement,
  key: string
): HTMLElement | null => {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(`[data-field="${key}"]`)
  );
  // Several fields are rendered twice in mutually exclusive branches; only one of the
  // two is ever laid out, and `offsetParent` is null for the hidden one.
  return nodes.find((node) => node.offsetParent !== null) || null;
};

const byDocumentOrder = (a: HTMLElement, b: HTMLElement): number =>
  // eslint-disable-next-line no-bitwise
  a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;

const isFocusable = (node: HTMLElement): boolean =>
  node.matches(FOCUSABLE_FIELD_SELECTOR);

/**
 * `data-field` sits on the outermost element of a control so it is present regardless of
 * that control's internal state. The dropdowns keep focus on their inner caption while the
 * time picker is focusable itself — so fall back to the first focusable descendant rather
 * than assuming either shape.
 */
const focusTargetFor = (node: HTMLElement): HTMLElement =>
  isFocusable(node)
    ? node
    : node.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR) || node;

const useMandatoryFieldFocus = ({
  containerRef,
  setErrors,
  values,
  itemsFieldKey = HANDLING_UNITS_FIELD_KEY,
}: UseMandatoryFieldFocusParams) => {
  useEffect(() => {
    if (!values) return;
    setErrors((prev) => pruneMandatoryMessages(prev, values));
  }, [values, setErrors]);

  /**
   * Drops a single mandatory message. Plain inputs clear themselves through
   * `handleFieldChange`, so this is for dropdowns and time pickers, which don't.
   */
  const clearMandatory = useCallback(
    (key: string) => {
      setErrors((prev) => {
        if (!isMandatoryMessage(prev?.[key])) return prev;
        return { ...prev, [key]: "" };
      });
    },
    [setErrors]
  );

  const applyValidation = useCallback(
    (result: ValidationResult): ApplyValidationOutcome => {
      setErrors((prev) => {
        const next = { ...prev };
        // Re-derive from scratch each click so fields fixed since the last attempt lose
        // their message even if they never passed through `handleFieldChange`.
        Object.keys(next).forEach((key) => {
          if (isMandatoryMessage(next[key])) next[key] = "";
        });
        result.missing.forEach(({ key, label }) => {
          next[key] = mandatoryMessage(label);
        });
        return next;
      });

      if (result.isValid) return { ok: true, focusItemCell: null };

      const container = containerRef.current;
      if (!container) return { ok: false, focusItemCell: null };

      const candidateKeys = [
        ...result.missing.map((entry) => entry.key),
        ...result.invalid.map((entry) => entry.key),
      ];

      const targets = candidateKeys
        .map((key) => firstVisible(container, key))
        .filter((node): node is HTMLElement => node !== null);

      const hasItemProblem = result.itemsEmpty || result.itemIssues.length > 0;
      const itemsTarget = hasItemProblem
        ? firstVisible(container, itemsFieldKey)
        : null;
      if (itemsTarget) targets.push(itemsTarget);

      if (targets.length === 0) return { ok: false, focusItemCell: null };

      targets.sort(byDocumentOrder);
      const first = targets[0];

      // Wait for the newly rendered error spans to lay out before scrolling, otherwise
      // the target lands off-centre.
      window.requestAnimationFrame(() => {
        first.scrollIntoView({ behavior: "smooth", block: "center" });
        const target = focusTargetFor(first);
        // Flag the focus as ours so a dropdown caption doesn't read it as the user
        // arriving and open its list over the message we just wrote. The dropdowns
        // clear the attribute themselves; the timeout covers every other control.
        target.setAttribute(VALIDATION_FOCUS_ATTR, "");
        // The page scrolls inside `.page__body` (overflow-y: auto), not the window.
        // Native "segmented" controls (date/time inputs) run their own focus-triggered
        // scroll-into-view that doesn't reliably honor `preventScroll` for that kind of
        // nested scroll container, which cancels our smooth scroll mid-flight and reads
        // as an instant jump. Delaying focus until the animation has had time to finish
        // means there is nothing left for that native behavior to jump to.
        window.setTimeout(() => {
          target.focus({ preventScroll: true });
          window.setTimeout(() => target.removeAttribute(VALIDATION_FOCUS_ATTR), 0);
        }, 450);
      });

      const firstIssue: ItemIssue | undefined = result.itemIssues[0];
      const focusItemCell =
        first === itemsTarget && firstIssue
          ? { rowIndex: firstIssue.rowIndex, columnKey: firstIssue.columnKeys[0] }
          : null;

      return { ok: false, focusItemCell };
    },
    [containerRef, itemsFieldKey, setErrors]
  );

  return { applyValidation, clearMandatory };
};

export default useMandatoryFieldFocus;
